import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { match } from 'ts-pattern';
import { type MaterialArrangement, MaterialArrangementForm } from './MaterialArrangementForm';

export const Route = createFileRoute('/console/$orgId/mspr/service-providers/details/$id/manage/edit/$arrangementId')({
    component: EditMaterialArrangement,
    params: {
        parse({ arrangementId }) {
            return { arrangementId: BigInt(arrangementId) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditMaterialArrangement() {
    const { id: providerId, arrangementId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const [open, setOpen] = useState(true);
    const [data, setData] = useState<MaterialArrangement>();

    const { data: materialArrangement, isPending: isLoading } =
        trpc.mspr.materialArrangement.getMaterialArrangement.useQuery(
            { id: arrangementId },
            { refetchOnWindowFocus: false },
        );
    const { mutate: updateMaterialArrangement, isPending: isUpdating } =
        trpc.mspr.materialArrangement.updateMaterialArrangement.useMutation({
            onError(error) {
                errorMessage.show(error);
            },
        });

    useEffect(() => {
        if (materialArrangement) {
            setData(materialArrangement);
        }
    }, [materialArrangement]);

    const handleSubmit = (values: MaterialArrangement) => {
        if (!arrangementId) return;
        updateMaterialArrangement(
            {
                id: arrangementId,
                set: {
                    exposureType: values.exposureType,
                    serviceDescription: values.serviceDescription,
                    contractTitle: values.contractTitle,
                    startDate: values.startDate,
                    endDate: values.endDate,
                    criticalOperationId: values.criticalOperationId,
                    arrangementCategoryId: values.arrangementCategoryId,
                },
            },
            {
                onSuccess: () => {
                    message.success('Material arrangement updated');
                    trpcUtils.mspr.materialArrangement.listMaterialArrangements.invalidate();
                    setOpen(false);
                },
            },
        );
    };

    const state = match({ isLoading, isUpdating })
        .with({ isUpdating: true }, () => 'submitting' as const)
        .with({ isLoading: true }, () => 'loading' as const)
        .otherwise(() => 'ready' as const);

    return (
        <PageController route={Route}>
            <MaterialArrangementForm
                open={open}
                mode="edit"
                providerId={providerId}
                data={data}
                state={state}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/mspr/service-providers/details/$id/manage',
                        params: { orgId, id: providerId },
                    });
                }}
            />
        </PageController>
    );
}
